import { memo } from "react";
import { AuthImage } from "./AuthImage";
import styles from "./Avatar.module.css";

interface AvatarProps {
  src?: string | null;
  name: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  online?: boolean;
  isBot?: boolean;
  className?: string;
}

// Generate consistent color from name
function nameToColor(name: string): string {
  const colors = [
    "#C0392B", "#E67E22", "#27AE60", "#2980B9",
    "#8E44AD", "#D35400", "#16A085", "#2C3E50",
    "#F39C12", "#1ABC9C", "#9B59B6", "#E74C3C",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length]!;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]![0]?.toUpperCase() ?? "?";
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export const Avatar = memo(function Avatar({ src, name, size = "md", online, isBot, className }: AvatarProps) {
  return (
    <div className={`${styles.avatar} ${styles[size]} ${className ?? ""}`}>
      {src ? (
        src.startsWith("/api/") ? (
          <AuthImage
            src={src}
            alt={name}
            className={styles.image}
          />
        ) : (
          <img
            src={src}
            alt={name}
            className={styles.image}
            loading="lazy"
          />
        )
      ) : (
        <div
          className={styles.fallback}
          style={{ backgroundColor: isBot ? "#5865F2" : nameToColor(name) }}
          title={name}
        >
          {isBot ? "🤖" : getInitials(name)}
        </div>
      )}
      {isBot && (
        <span className={styles.botBadge} title="Bot">⚡</span>
      )}
      {!isBot && online !== undefined && (
        <span
          className={`${styles.status} ${online ? styles.online : styles.offline}`}
          aria-label={online ? "Online" : "Offline"}
        />
      )}
    </div>
  );
});
